/**
 * A show's timezone, and the arithmetic between a wall clock and an instant.
 *
 * `Event.scheduledStart` is an instant. What an organizer means by "8 PM" is a
 * wall clock in the place the show is played, and the two are only the same
 * thing once you know where that is — which is why `Events.timezone` exists
 * (ADR-0043). Before it, every server-rendered time fell back to UTC, so an
 * 8 PM Seattle show reached an act's inbox as "3:00 AM" the next day.
 *
 * Shared by the backend (imported by relative path, like `cookieNames.ts`) and
 * the admin app, so the create form, the edit form and the server agree on
 * what a valid zone is and on how a wall clock becomes an instant.
 *
 * Pure and import-free on purpose: `tests/unit` compiles under the backend
 * tsconfig, which has no `@showliner/shared` mapping. Built on `Intl` alone,
 * with no timezone library, because the admin app has none and the one place
 * that needs DST-correct expansion (`recurrenceService`) already uses Luxon
 * server-side. Unit-tested in tests/unit/showTimezone.test.ts.
 */

/** A date and time as read off a clock on the venue wall. Month is 1-12. */
export interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

/**
 * The canonical IANA name for a zone, or null when it isn't one.
 *
 * Canonicalises through `Intl` so `america/los_angeles` and
 * `America/Los_Angeles` store as one value. Anything that isn't a non-empty
 * string, or that `Intl` refuses, is null, never thrown: a malformed zone on a
 * write path is refused by the caller, and on a read path it must degrade to
 * "unknown", not take the page down.
 */
export function normalizeTimeZone(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 64) return null;
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: trimmed }).resolvedOptions().timeZone;
  } catch {
    return null;
  }
}

/** The zone the current runtime is in: the organizer's, in a browser. */
export function browserTimeZone(): string | null {
  try {
    return normalizeTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  } catch {
    return null;
  }
}

/**
 * The zone an event is played in, if one is on record.
 *
 * `timezone` is the record. A recurring event's rule carried a zone before the
 * column existed, so it is the fallback for rows the backfill could not reach.
 */
export function eventTimeZone(event: {
  timezone?: unknown;
  recurrenceRule?: { timezone?: unknown } | null;
} | null | undefined): string | null {
  if (!event) return null;
  return normalizeTimeZone(event.timezone) ?? normalizeTimeZone(event.recurrenceRule?.timezone);
}

/** The wall clock in `timeZone` at `instant`, or null for an invalid zone or date. */
export function instantToWallClock(instant: Date, timeZone: string): WallClock | null {
  if (Number.isNaN(instant.getTime())) return null;
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      // h23, not hour12:false — some engines render midnight as "24" under
      // hour12:false, which would put the wall clock on the wrong day.
      hourCycle: 'h23',
    } as Intl.DateTimeFormatOptions).formatToParts(instant);
  } catch {
    return null;
  }
  const read = (type: string): number => Number(parts.find((p) => p.type === type)?.value);
  const wall: WallClock = {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hour: read('hour') % 24,
    minute: read('minute'),
  };
  return Object.values(wall).every(Number.isFinite) ? wall : null;
}

/** Minutes `timeZone` is ahead of UTC at `instant` (Seattle in summer: -420). */
export function timeZoneOffsetMinutes(instant: Date, timeZone: string): number | null {
  const wall = instantToWallClock(instant, timeZone);
  if (!wall) return null;
  const asUtc = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute);
  const instantToMinute = Math.floor(instant.getTime() / 60_000) * 60_000;
  return Math.round((asUtc - instantToMinute) / 60_000);
}

/**
 * The instant a wall clock in `timeZone` names: "8 PM on Oct 17 in Seattle"
 * becomes 03:00 UTC on Oct 18.
 *
 * Two passes, because the offset depends on the instant being solved for. On a
 * DST transition the answer is deterministic rather than clever: a time that
 * never happens (2:30 AM on spring-forward night) resolves an hour later, and
 * a time that happens twice resolves to the first. Nobody books a show for
 * 2:30 AM on the second Sunday of March, and the edit form would show the
 * resolved time back to them if they did.
 *
 * An invalid zone falls back to UTC rather than throwing, matching how every
 * reader treats an unknown zone.
 */
export function wallClockToInstant(wall: WallClock, timeZone: string | null): Date {
  const guess = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute);
  if (!timeZone) return new Date(guess);
  const first = timeZoneOffsetMinutes(new Date(guess), timeZone);
  if (first === null) return new Date(guess);
  const candidate = guess - first * 60_000;
  const second = timeZoneOffsetMinutes(new Date(candidate), timeZone);
  if (second === null || second === first) return new Date(candidate);
  // The offset changed between the guess and the candidate: we straddle a
  // transition. Re-solve with the second offset, and if that one doesn't
  // round-trip either the wall clock is inside a gap. Subtracting the SMALLER
  // offset gives the later instant (in either hemisphere), which lands the
  // time just after the clocks jump.
  const retry = guess - second * 60_000;
  const check = timeZoneOffsetMinutes(new Date(retry), timeZone);
  if (check === second) return new Date(retry);
  return new Date(guess - Math.min(first, second) * 60_000);
}

/**
 * A short, human name for a zone: "Los Angeles (PDT)", "London (GMT+1)".
 *
 * The abbreviation is taken at `at` so it reads correctly for the show's own
 * date (PST in December, PDT in July). Falls back to the raw id.
 */
export function formatTimeZoneLabel(timeZone: string, at: Date = new Date()): string {
  const city = timeZone.split('/').pop()?.replace(/_/g, ' ') || timeZone;
  const short = timeZoneAbbreviation(timeZone, at);
  return short && short !== city ? `${city} (${short})` : city;
}

/** "PDT", "GMT+1", or null when the runtime can't say. */
export function timeZoneAbbreviation(timeZone: string, at: Date = new Date()): string | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' }).formatToParts(at);
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? null;
  } catch {
    return null;
  }
}

/**
 * A listed show's start, in the show's own zone: "Fri, Oct 16, 8:00 PM", with
 * the zone named ("8:00 PM EDT") when it differs from the viewer's (#863).
 *
 * For lists of shows on public pages, read by visitors anywhere. Formatting in
 * the visitor's zone told a Seattle reader a New York 8 PM show was at 5 PM.
 * Same rule as `EventHeader`'s `formatSchedule`. A show with no zone on record
 * falls back to the viewer's own, as it always did. `viewerZone` is a
 * parameter so the rule is testable; callers pass `browserTimeZone()`.
 */
export function formatListedShowTime(
  start: Date | string | null | undefined,
  showZone: string | null | undefined,
  viewerZone: string | null | undefined,
): string {
  if (!start) return '';
  const at = new Date(start);
  if (Number.isNaN(at.getTime())) return '';
  const zone = normalizeTimeZone(showZone) ?? undefined;
  const text = at.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
    timeZone: zone,
  });
  if (!zone || zone === normalizeTimeZone(viewerZone)) return text;
  return `${text} ${timeZoneAbbreviation(zone, at) ?? zone}`;
}

/**
 * Every zone the runtime knows, for a picker. Falls back to a short list when
 * `Intl.supportedValuesOf` is unavailable (older Safari), which still covers
 * the zones an organizer is overwhelmingly likely to need, plus their own.
 */
export function listTimeZones(include: Array<string | null | undefined> = []): string[] {
  const supported = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf;
  let zones: string[];
  try {
    zones = supported ? supported('timeZone') : [...FALLBACK_ZONES];
  } catch {
    zones = [...FALLBACK_ZONES];
  }
  for (const extra of include) {
    const z = normalizeTimeZone(extra);
    if (z && !zones.includes(z)) zones.push(z);
  }
  return zones.sort();
}

/**
 * The clock time of an instant in a show's zone, as "HH:mm" (UTC when the
 * show has no zone), or null.
 */
export function clockInZone(value: string | Date | null | undefined, timeZone: string | null): string | null {
  if (value == null || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const wall = instantToWallClock(d, timeZone ?? 'UTC');
  if (!wall) return null;
  return `${String(wall.hour).padStart(2, '0')}:${String(wall.minute).padStart(2, '0')}`;
}

/** How far before a show's start a typed clock time can be and still be that day. */
export const SAME_DAY_LEAD_HOURS = 6;

/**
 * The instant a clock time ("21:15") names on a show: the show's local date,
 * or the next day when the time is more than SAME_DAY_LEAD_HOURS before the
 * show starts. A 12:30 AM closing set on a Friday show is Saturday 12:30 AM,
 * not Friday morning (CONTEXT.md → Set time).
 *
 * `notBefore` moves the result a day at a time until it is after that instant,
 * so an end typed as "1:00" after a "23:30" start lands the next morning.
 * Null for an unreadable time or a show with no start.
 */
export function showClockToInstant(
  clock: string,
  showStart: string | Date | null | undefined,
  timeZone: string | null,
  notBefore?: string | Date | null,
): string | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(clock.trim());
  if (!match || showStart == null || showStart === '') return null;
  const start = showStart instanceof Date ? showStart : new Date(showStart);
  if (Number.isNaN(start.getTime())) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return null;

  const day = instantToWallClock(start, timeZone ?? 'UTC');
  if (!day) return null;
  const onDay = (offsetDays: number): Date => {
    const d = new Date(Date.UTC(day.year, day.month - 1, day.day + offsetDays));
    return wallClockToInstant(
      { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate(), hour, minute },
      timeZone,
    );
  };

  let offset = 0;
  let instant = onDay(0);
  if (instant.getTime() < start.getTime() - SAME_DAY_LEAD_HOURS * 3_600_000) instant = onDay(++offset);
  const floor = notBefore == null || notBefore === '' ? null : new Date(notBefore);
  if (floor && !Number.isNaN(floor.getTime())) {
    // A week of tries is far past any real set; it only stops a bad floor looping.
    for (let i = 0; i < 7 && instant.getTime() <= floor.getTime(); i++) instant = onDay(++offset);
  }
  return instant.toISOString();
}

const FALLBACK_ZONES = [
  'America/Anchorage',
  'America/Chicago',
  'America/Denver',
  'America/Halifax',
  'America/Los_Angeles',
  'America/New_York',
  'America/Phoenix',
  'America/St_Johns',
  'America/Toronto',
  'America/Vancouver',
  'Australia/Sydney',
  'Europe/Berlin',
  'Europe/Dublin',
  'Europe/London',
  'Europe/Paris',
  'Pacific/Honolulu',
  'UTC',
] as const;
